import React, { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import "./PresentationModal.scss";
import { IoCloseOutline } from "react-icons/io5";
import draftToHtml from "draftjs-to-html";
import RestClient from "../../../@core/restClient";

const chordCheck = /([A-G](#|b)?(m|maj|min|dim|aug|sus|add)?\d*(\/[A-G](#|b)?)?)+/;
const chordExclude = /[ceghklnoqrtvwyz]/;
const sectionMarker = /^[%$@*]/;
const sectionWords = /^(verse|chorus|bridge|coda|tag|vamp|intro|outro|pre[-\s]?chorus|куплет|приспів|бридж|брідж|кода|вступ|тег|аутро)(\s+\d+)?[:\s]*$/i;

interface BgChoice { bgHex: string; textHex: string; preview: string; toHex?: string; }

const COLORS: BgChoice[] = [
  { bgHex: "000000", textHex: "#ffffff", preview: "#000000" },
  { bgHex: "1a1a2e", textHex: "#ffffff", preview: "#1a1a2e" },
  { bgHex: "0d4b6e", textHex: "#ffffff", preview: "#0d4b6e" },
  { bgHex: "0d6e6e", textHex: "#ffffff", preview: "#0d6e6e" },
  { bgHex: "1b5e20", textHex: "#ffffff", preview: "#1b5e20" },
  { bgHex: "4a148c", textHex: "#ffffff", preview: "#4a148c" },
  { bgHex: "880e4f", textHex: "#ffffff", preview: "#880e4f" },
  { bgHex: "b71c1c", textHex: "#ffffff", preview: "#b71c1c" },
  { bgHex: "e65100", textHex: "#ffffff", preview: "#e65100" },
  { bgHex: "f57f17", textHex: "#000000", preview: "#f57f17" },
  { bgHex: "ffffff", textHex: "#1a1a2e", preview: "#ffffff" }
];

const GRADIENTS: BgChoice[] = [
  { bgHex: "0f0c29", toHex: "302b63", textHex: "#ffffff", preview: "linear-gradient(135deg,#0f0c29,#302b63)" },
  { bgHex: "1a1a2e", toHex: "e94560", textHex: "#ffffff", preview: "linear-gradient(135deg,#1a1a2e,#e94560)" },
  { bgHex: "200122", toHex: "6f0000", textHex: "#ffffff", preview: "linear-gradient(135deg,#200122,#6f0000)" },
  { bgHex: "000428", toHex: "004e92", textHex: "#ffffff", preview: "linear-gradient(135deg,#000428,#004e92)" },
  { bgHex: "093028", toHex: "237a57", textHex: "#ffffff", preview: "linear-gradient(135deg,#093028,#237a57)" },
  { bgHex: "0f2027", toHex: "2c5364", textHex: "#ffffff", preview: "linear-gradient(135deg,#0f2027,#2c5364)" },
  { bgHex: "4a00e0", toHex: "8e2de2", textHex: "#ffffff", preview: "linear-gradient(135deg,#4a00e0,#8e2de2)" },
  { bgHex: "f7971e", toHex: "ffd200", textHex: "#000000", preview: "linear-gradient(135deg,#f7971e,#ffd200)" },
  { bgHex: "11998e", toHex: "ffffff", textHex: "#003333", preview: "linear-gradient(135deg,#11998e,#ffffff)" }
];

const parseSlides = (songText: string): string[][] => {
  if (!songText) return [];
  let html: string;
  try {
    html = draftToHtml(JSON.parse(songText));
  } catch {
    return [];
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const paragraphs = Array.from(doc.querySelectorAll("p"));
  const slides: string[][] = [];
  let current: string[] = [];

  paragraphs.forEach((p) => {
    const raw = (p.textContent || "").trim();
    if (!raw) {
      if (current.length > 0) { slides.push([...current]); current = []; }
      return;
    }
    if (/^\*/.test(raw)) return;
    const text = raw.replace(/^[%$@]\s*/, "");
    if (!text) return;
    if (sectionWords.test(text)) return;
    if (chordCheck.test(text) && !chordExclude.test(text)) return;
    current.push(text);
  });

  if (current.length > 0) slides.push(current);
  return slides;
};

const applyMaxLines = (slides: string[][], max: number): string[][] => {
  const result: string[][] = [];
  slides.forEach((slide) => {
    if (slide.length <= max) {
      result.push(slide);
    } else {
      for (let i = 0; i < slide.length; i += max) {
        result.push(slide.slice(i, i + max));
      }
    }
  });
  return result;
};

const PresentationModal = ({ song, onClose, collabPermission, onUploaded }: any) => {
  const [maxLines, setMaxLines] = useState(4);
  const [fontSize, setFontSize] = useState(36);
  const [bg, setBg] = useState<BgChoice>(COLORS[1]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const raw = useMemo(() => parseSlides(song?.text), [song?.text]);
  const slides = useMemo(() => applyMaxLines(raw, maxLines), [raw, maxLines]);

  const generatePptx = async () => {
    setIsGenerating(true);
    try {
      const { generateAndSavePptx } = await import("../../../utils/pptxBuilder");
      const blob = await generateAndSavePptx(slides, bg.bgHex, bg.textHex, fontSize, song?.title || "presentation", bg.toHex);
      if (collabPermission) {
        setPendingBlob(blob);
      } else {
        onClose();
      }
    } catch (e) {
      console.error(e);
    }
    setIsGenerating(false);
  };

  const uploadToSong = async () => {
    if (!pendingBlob || !song?.id) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("songId", song.id);
      formData.append("type", "presentation");
      formData.append("name", song?.title || "presentation");
      formData.append("file", pendingBlob, `${song?.title || "presentation"}.pptx`);
      await RestClient.addMedia(formData);
      onUploaded?.();
    } catch (e) {
      console.error(e);
    }
    setIsUploading(false);
    onClose();
  };

  return (
    <div className="presentation-modal" onClick={onClose}>
      <div className="presentation-modal-inner" onClick={(e) => e.stopPropagation()}>
        <IoCloseOutline className="presentation-modal-close" onClick={onClose} />
        <h2 className="presentation-modal-title">{song?.title}</h2>
        <p className="presentation-modal-subtitle">{slides.length} слайдів</p>

        <div className="presentation-modal-controls">
          <span className="presentation-modal-controls-label">Рядків на слайді:</span>
          <button className="pres-stepper-btn" onClick={() => setMaxLines((v) => Math.max(1, v - 1))}>−</button>
          <span className="pres-stepper-value">{maxLines}</span>
          <button className="pres-stepper-btn" onClick={() => setMaxLines((v) => Math.min(12, v + 1))}>+</button>

          <span className="presentation-modal-controls-label" style={{ marginLeft: "2rem" }}>Розмір шрифту:</span>
          <button className="pres-stepper-btn" onClick={() => setFontSize((v) => Math.max(12, v - 2))}>−</button>
          <span className="pres-stepper-value">{fontSize}</span>
          <button className="pres-stepper-btn" onClick={() => setFontSize((v) => Math.min(72, v + 2))}>+</button>
        </div>

        <div className="pres-bg-row">
          {COLORS.map((option) => (
            <button
              key={option.bgHex}
              className={`pres-color-swatch${bg === option ? " pres-swatch--active" : ""}`}
              style={{ background: option.preview }}
              onClick={() => setBg(option)}
            />
          ))}
        </div>
        <div className="pres-bg-row">
          {GRADIENTS.map((option, i) => (
            <button
              key={i}
              className={`pres-gradient-swatch${bg === option ? " pres-swatch--active" : ""}`}
              style={{ background: option.preview }}
              onClick={() => setBg(option)}
            />
          ))}
        </div>

        <div className="presentation-modal-slides">
          {slides.length === 0 && (
            <p className="presentation-modal-empty">Текст пісні відсутній</p>
          )}
          {slides.map((slide, i) => (
            <div key={i} className="pres-slide" style={{ background: bg.preview }}>
              <span className="pres-slide-number" style={{ color: bg.textHex + "66" }}>{i + 1}</span>
              {slide.map((line, j) => (
                <p key={j} className="pres-slide-line" style={{ fontSize: `${(fontSize * 20.4 / 540).toFixed(2)}rem`, color: bg.textHex }}>{line}</p>
              ))}
            </div>
          ))}
        </div>

        <div className="action-btns">
          <button
            className="btn-mcolor"
            onClick={generatePptx}
            disabled={isGenerating || slides.length === 0}
          >
            {isGenerating ? "Генерується..." : "Згенерувати"}
          </button>
          <button className="btn-plain cancel-btn" onClick={onClose}>
            Скасувати
          </button>
        </div>

      </div>

      {pendingBlob && ReactDOM.createPortal(
        <div className="pres-upload-overlay">
          <div className="pres-upload-box">
            <p className="pres-upload-box-text">Додати презентацію до медіафайлів пісні?</p>
            <div className="action-btns">
              <button className="btn-mcolor" onClick={uploadToSong} disabled={isUploading}>
                {isUploading ? "Завантаження..." : "Додати"}
              </button>
              <button className="btn-plain cancel-btn" onClick={onClose}>Пропустити</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PresentationModal;
